import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { CreditCard, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ProposalStatus, proposalApi } from '@/api/commercial/proposals';
import { PaymentConfigModal } from '@/components/dialogs/commercial/payment-config/payment-config-modal';
import { ProposalTable } from '@/components/table/commercial/proposal-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableSkeleton } from '@/components/ui/skeleton';

type ProposalStatusFilter = 'Todos' | ProposalStatus;

const STATUS_OPTIONS: { value: ProposalStatusFilter; label: string }[] = [
  { value: 'Todos', label: 'Todos' },
  { value: ProposalStatus.OPEN, label: 'Aberta' },
  { value: ProposalStatus.PENDING_ACCEPT, label: 'Aguardando aceite' },
  { value: ProposalStatus.PENDING_CREDIT, label: 'Aguardando Análise de Crédito' },
  { value: ProposalStatus.FINISHED, label: 'Finalizada' },
  { value: ProposalStatus.CANCELED, label: 'Cancelada' },
];

export function ProposalListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentConfigOpen, setPaymentConfigOpen] = useState(false);
  // Filter states from URL
  const [filterClient, setFilterClient] = useState(searchParams.get('client') || '');
  const [filterCode, setFilterCode] = useState(searchParams.get('code') || '');
  const [filterStatus, setFilterStatus] = useState<ProposalStatusFilter>(
    (searchParams.get('status') as ProposalStatusFilter) || 'Todos',
  );
  const [filterStartDate, setFilterStartDate] = useState(searchParams.get('start_date') || '');
  const [filterEndDate, setFilterEndDate] = useState(searchParams.get('end_date') || '');
  const [filterSeller, setFilterSeller] = useState(searchParams.get('seller') || '');

  const page = Number(searchParams.get('page')) || 1;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['proposals', Object.fromEntries(searchParams)],
    queryFn: () => {
      const params: Record<string, string | number> = {};
      const client = searchParams.get('client');
      const code = searchParams.get('code');
      const status = searchParams.get('status');
      const startDate = searchParams.get('start_date');
      const endDate = searchParams.get('end_date');
      const seller = searchParams.get('seller');
      const pageParam = searchParams.get('page');

      if (client) params.search = client;
      if (code) params.code = Number(code);
      if (status && status !== 'Todos') params.status = status;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (seller) params.seller = seller;
      if (pageParam) params.page = Number(pageParam);

      return proposalApi.findMany(params);
    },
  });

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    params.set('page', '1');

    if (filterClient) params.set('client', filterClient);
    if (filterCode) params.set('code', filterCode);
    if (filterStatus && filterStatus !== 'Todos') params.set('status', filterStatus);
    if (filterStartDate) params.set('start_date', filterStartDate);
    if (filterEndDate) params.set('end_date', filterEndDate);
    if (filterSeller) params.set('seller', filterSeller);

    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilterClient('');
    setFilterCode('');
    setFilterStatus('Todos');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterSeller('');
    setSearchParams(new URLSearchParams());
  };

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-500">Erro ao carregar propostas</p>
        <Button onClick={() => refetch()} className="mt-4">
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Propostas</h1>
          <p className="text-muted-foreground">Visualize e gerencie as propostas comerciais</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPaymentConfigOpen(true)}>
            <CreditCard className="h-4 w-4 mr-2" />
            Config de pgto
          </Button>
          <Button asChild className="bg-black text-white hover:bg-gray-800">
            <Link to="/commercial/proposal/form/create">
              <Plus className="h-4 w-4 mr-2" />
              Nova Proposta
            </Link>
          </Button>
        </div>
        <PaymentConfigModal open={paymentConfigOpen} onOpenChange={setPaymentConfigOpen} />
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros de busca</CardTitle>
          <CardDescription>Defina os critérios e clique em "Pesquisar"</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div>
                <Input
                  label="Cliente"
                  placeholder="Nome, CPF ou CNPJ"
                  value={filterClient}
                  onChange={(e) => setFilterClient(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Identificador"
                  placeholder="ID da proposta"
                  value={filterCode}
                  onChange={(e) => setFilterCode(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <span className="text-sm font-medium text-gray-700">Status</span>
                <Select
                  value={filterStatus}
                  onValueChange={(value) => setFilterStatus(value as ProposalStatusFilter)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Input
                  label="Data início"
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Data fim"
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                />
              </div>
              <div>
                <Input
                  label="Vendedor"
                  placeholder="Nome do vendedor"
                  value={filterSeller}
                  onChange={(e) => setFilterSeller(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className="bg-black text-white hover:bg-gray-800">
                <Search className="h-4 w-4 mr-2" />
                Pesquisar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <TableSkeleton rows={10} />
        </div>
      ) : data ? (
        <ProposalTable data={data} currentPage={page} onPageChange={handlePageChange} />
      ) : null}
    </motion.div>
  );
}
